import React from 'react';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../screens/ThemeContext';

// Détecte à la fois :
//   1) Le markdown [label](url)
//   2) Les URLs brutes (https://..., http://..., www....)
// Tout est rendu cliquable.
function MarkdownText({ text, style, linkColor }) {
  const segments = React.useMemo(() => {
    const out = [];
    if (!text || typeof text !== 'string') return out;
    const re = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|((?:https?:\/\/|www\.)[^\s<>"']+[^\s<>"'.,;:!?)])/g;
    let last = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) out.push({ type: 'text', value: text.slice(last, m.index) });
      if (m[1] && m[2]) {
        out.push({ type: 'link', label: m[1], url: m[2] });
      } else if (m[3]) {
        const url = m[3].startsWith('www.') ? 'https://' + m[3] : m[3];
        out.push({ type: 'link', label: m[3], url });
      }
      last = re.lastIndex;
    }
    if (last < text.length) out.push({ type: 'text', value: text.slice(last) });
    if (out.length === 0) out.push({ type: 'text', value: text });
    return out;
  }, [text]);

  return (
    <span style={style}>
      {segments.map((s, i) =>
        s.type === 'link' ? (
          <a
            key={i}
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: linkColor, textDecoration: 'underline', fontWeight: 600 }}
          >
            {s.label}
          </a>
        ) : (
          <span key={i}>{s.value}</span>
        )
      )}
    </span>
  );
}

export default function AnnouncementDetailsScreen({ route, navigation }) {
  const { announcement } = route.params;
  const { theme } = useTheme();

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleCall = () => {
    window.open(`tel:${announcement.contact.telephone}`);
  };

  const handleWhatsapp = () => {
    window.open(`https://wa.me/${announcement.contact.whatsapp}`);
  };

  const handleEmail = () => {
    window.open(`mailto:${announcement.contact.email}`);
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      backgroundColor: theme.background,
      overflow: 'hidden',
    },
    scrollContainer: {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
      position: 'relative',
    },
    coverImage: {
      width: '100%',
      height: '250px',
      objectFit: 'cover',
    },
    backButton: {
      position: 'absolute',
      top: '20px',
      left: '20px',
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      borderRadius: '10px',
      padding: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
    },
    content: {
      flex: 1,
      padding: '20px',
      marginTop: '-40px',
      position: 'relative',
      zIndex: 1,
    },
    centerCard: {
      backgroundColor: theme.surface,
      padding: '15px',
      borderRadius: '16px',
      marginBottom: '20px',
      border: `1px solid ${theme.border}`,
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    },
    centerHeader: {
      textAlign: 'center',
    },
    centerName: {
      fontSize: '20px',
      fontWeight: 'bold',
      marginBottom: '4px',
      color: theme.text,
    },
    centerAddress: {
      fontSize: '14px',
      color: theme.textSecondary,
    },
    section: {
      backgroundColor: theme.surface,
      padding: '15px',
      borderRadius: '16px',
      marginBottom: '20px',
      border: `1px solid ${theme.border}`,
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    },
    title: {
      fontSize: '22px',
      fontWeight: 'bold',
      marginBottom: '12px',
      color: theme.text,
    },
    description: {
      fontSize: '14px',
      lineHeight: '22px',
      marginBottom: '20px',
      color: theme.text,
    },
    subsection: {
      marginTop: '20px',
    },
    subtitle: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '12px',
      color: theme.text,
    },
    concoursGrid: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: '8px',
    },
    concoursBadge: {
      backgroundColor: theme.background,
      paddingLeft: '12px',
      paddingRight: '12px',
      paddingTop: '6px',
      paddingBottom: '6px',
      borderRadius: '20px',
    },
    concoursText: {
      fontSize: '14px',
      fontWeight: '500',
      color: theme.primary,
    },
    dateInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    dateRow: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '10px',
    },
    dateText: {
      fontSize: '14px',
      color: theme.text,
    },
    avantageItem: {
      marginBottom: '16px',
    },
    avantageTitle: {
      fontSize: '16px',
      fontWeight: '600',
      marginBottom: '4px',
      color: theme.text,
    },
    avantageDescription: {
      fontSize: '14px',
      lineHeight: '20px',
      color: theme.textSecondary,
    },
    contactButtons: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      marginTop: '10px',
    },
    contactButton: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '12px',
      borderRadius: '8px',
      gap: '8px',
      cursor: 'pointer',
      border: 'none',
      fontSize: '14px',
      fontWeight: '600',
      color: '#fff',
      textDecoration: 'none',
      transition: 'opacity 0.2s',
    },
    contactButtonCall: {
      backgroundColor: theme.primary,
    },
    contactButtonWhatsapp: {
      backgroundColor: '#25D366',
    },
    contactButtonEmail: {
      backgroundColor: '#EA4335',
    },
    '@media (min-width: 768px)': {
      contactButtons: {
        flexDirection: 'row',
      },
      contactButton: {
        flex: 1,
      },
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.scrollContainer}>
        <img
          src={announcement.imageUrl}
          alt="Annonce"
          style={styles.coverImage}
        />

        <div style={styles.backButton} onClick={() => navigation.goBack()}>
          <MaterialCommunityIcons name="arrow-left" size={24} color="#fff" />
        </div>

        <div style={styles.content}>
          {/* Informations du centre */}
          <div style={styles.centerCard}>
            <div style={styles.centerHeader}>
              <div style={styles.centerName}>
                {announcement.centerInfo.centerName}
              </div>
              <div style={styles.centerAddress}>
                {announcement.centerInfo.centerAddress}
              </div>
            </div>
          </div>

          {/* Détails de l'annonce */}
          <div style={styles.section}>
            <h1 style={styles.title}>{announcement.titre}</h1>
            <p style={styles.description}>
              <MarkdownText text={announcement.description} style={styles.description} linkColor={theme.primary} />
            </p>

            {/* Concours préparés */}
            <div style={styles.subsection}>
              <div style={styles.subtitle}>Concours préparés</div>
              <div style={styles.concoursGrid}>
                {announcement.concours.map((concours, index) => (
                  <div key={index} style={styles.concoursBadge}>
                    <span style={styles.concoursText}>
                      {concours}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Période - Commenté dans l'original */}
            {/* <div style={styles.subsection}>
              <div style={styles.subtitle}>Période de formation</div>
              <div style={styles.dateInfo}>
                <div style={styles.dateRow}>
                  <MaterialCommunityIcons 
                    name="calendar-start" 
                    size={20} 
                    color={theme.primary} 
                  />
                  <span style={styles.dateText}>
                    Début : {formatDate(announcement.dateDebut)}
                  </span>
                </div>
                <div style={styles.dateRow}>
                  <MaterialCommunityIcons 
                    name="calendar-end" 
                    size={20} 
                    color={theme.primary} 
                  />
                  <span style={styles.dateText}>
                    Fin : {formatDate(announcement.dateFin)}
                  </span>
                </div>
              </div>
            </div> */}

            {/* Avantages */}
            {announcement.avantages && announcement.avantages.length > 0 && (
              <div style={styles.subsection}>
                <div style={styles.subtitle}>Pourquoi nous choisir ?</div>
                {announcement.avantages.map((avantage, index) => (
                  <div key={index} style={styles.avantageItem}>
                    <div style={styles.avantageTitle}>
                      <MarkdownText text={avantage.titre} style={styles.avantageTitle} linkColor={theme.primary} />
                    </div>
                    <div style={styles.avantageDescription}>
                      <MarkdownText text={avantage.description} style={styles.avantageDescription} linkColor={theme.primary} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Liens utiles */}
            {announcement.liensUtiles && announcement.liensUtiles.length > 0 && (
              <div style={styles.subsection}>
                <div style={styles.subtitle}>Liens utiles</div>
                {announcement.liensUtiles.map((lien, index) => (
                  <a
                    key={index}
                    href={lien.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: `1px solid ${theme.border}`,
                      backgroundColor: theme.primary + '08',
                      marginTop: 8,
                      textDecoration: 'none',
                      color: 'inherit',
                    }}
                  >
                    <div style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: theme.primary + '15', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <MaterialCommunityIcons name="link-variant" size={18} color={theme.primary} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: theme.text, fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {lien.titre || lien.url}
                      </div>
                      <div style={{ color: theme.textSecondary, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {lien.url}
                      </div>
                    </div>
                    <MaterialCommunityIcons name="open-in-new" size={16} color={theme.primary} />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Contact */}
          <div style={styles.section}>
            <div style={styles.subtitle}>Contact</div>
            <div style={styles.contactButtons}>
              <button 
                style={{...styles.contactButton, ...styles.contactButtonCall}}
                onClick={handleCall}
                onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                onMouseLeave={(e) => e.target.style.opacity = '1'}
              >
                <MaterialCommunityIcons name="phone" size={24} color="#fff" />
                <span>Appeler</span>
              </button>

              <button 
                style={{...styles.contactButton, ...styles.contactButtonWhatsapp}}
                onClick={handleWhatsapp}
                onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                onMouseLeave={(e) => e.target.style.opacity = '1'}
              >
                <MaterialCommunityIcons name="whatsapp" size={24} color="#fff"/>
                <span>WhatsApp</span>
              </button>

              <button 
                style={{...styles.contactButton, ...styles.contactButtonEmail}}
                onClick={handleEmail}
                onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                onMouseLeave={(e) => e.target.style.opacity = '1'}
              >
                <MaterialCommunityIcons name="email" size={24} color="#fff" />
                <span>Email</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}